import type { Initiative } from '../../../../api/initiative-client/initiative-client.type';
import { InitiativeStatus } from '../../../../api/initiative-client/initiative-client.type';
import type { Team } from '../../../../api/projects-client/projects-client.type';
import i18n from '../../../../base-dictionary';
import { formatInitiativeDate } from '../../strategy-v2.helpers';
import { getContributingTeams } from '../main-section/main-section.helpers';

/**
 * Generates an array of label-value pairs containing initiative information
 *
 * @param initiative - The initiative object containing the data
 * @returns An array of objects with label and value properties representing initiative details
 *          Each object contains:
 *          - label: The display label for the field
 *          - value: The corresponding value from the initiative object
 */
const getInitiativeInfo = (
  initiative: Initiative,
  teams: Team[],
): { label: string; value: string | number | null | undefined }[] => {
  return [
    {
      label: i18n.t('strategy.initiative_performance.start_date'),
      value: formatInitiativeDate(initiative.start_date),
    },
    { label: i18n.t('strategy.initiative_performance.end_date'), value: formatInitiativeDate(initiative.end_date) },
    {
      label: i18n.t('strategy.initiative_performance.activity_start_date'),
      value:
        initiative.effective_start_date && initiative.effective_start_date !== initiative.start_date
          ? formatInitiativeDate(initiative.effective_start_date)
          : i18n.t('strategy.initiatives.labels.same_as_start_date', 'Same as Start Date'),
    },
    {
      label: i18n.t('strategy.initiative_performance.activity_end_date'),
      value:
        initiative.effective_end_date && initiative.effective_end_date !== initiative.end_date
          ? formatInitiativeDate(initiative.effective_end_date)
          : i18n.t('strategy.initiatives.labels.same_as_end_date', 'Same as End Date'),
    },
    {
      label: i18n.t('strategy.initiatives.labels.status_by_points', 'Status (By Points)'),
      value:
        getInitiativeStatus(initiative.status?.by_points?.status) || i18n.t('strategy.initiatives.labels.no_data', '-'),
    },
    {
      label: i18n.t('strategy.initiatives.labels.status_by_tasks', 'Status (By Tasks)'),
      value:
        getInitiativeStatus(initiative.status?.by_tasks?.status) || i18n.t('strategy.initiatives.labels.no_data', '-'),
    },
    {
      label: i18n.t('strategy.initiative_performance.cost_to_date'),
      value: i18n.t('common.currency.dollar_value', { value: initiative.costs_to_date?.initiative || 0 }),
    },
    {
      label: i18n.t('strategy.initiatives.form.business_value_label'),
      value: initiative.relative_business_value ?? i18n.t('strategy.initiatives.labels.no_data', '-'),
    },
    {
      label: i18n.t('common.teams'),
      value: getContributingTeams(initiative, teams) || i18n.t('strategy.initiatives.labels.no_data', '-'),
    },
  ];
};

/**
 * Helper method to get the string of the initiative status
 *
 * @param {InitiativeStatus| undefined} status enum choice of the initiative status
 * @returns {string} the label string of the status
 */
const getInitiativeStatus = (status: InitiativeStatus | undefined): string => {
  const labelMapping = {
    [InitiativeStatus.AtRisk]: i18n.t('strategy.initiatives.statuses.at_risk', 'At Risk'),
    [InitiativeStatus.OffTrack]: i18n.t('strategy.initiatives.statuses.off_track', 'Off Track'),
    [InitiativeStatus.OnTrack]: i18n.t('strategy.initiatives.statuses.on_track', 'On Track'),
    [InitiativeStatus.Unknown]: i18n.t('strategy.initiatives.statuses.unknown', 'Unknown'),
    [InitiativeStatus.Empty]: i18n.t('strategy.initiatives.statuses.empty', 'Empty'),
  };

  return status ? labelMapping[status] : i18n.t('strategy.initiatives.statuses.unknown', 'Unknown');
};

export { getInitiativeInfo, getInitiativeStatus };
